/**
 * Store Selector Module
 * Handles store selection via modal, pincode lookup, and localStorage/cart attribute management
 */

class StoreSelector {
  constructor() {
    this.stores = [
      {
        id: 1,
        name: "Malappuram Store",
        pincode: 676505,
        lat: 11.0735,
        lng: 76.068,
      },
      {
        id: 2,
        name: "Calicut Store",
        pincode: 673001,
        lat: 11.2588,
        lng: 75.7804,
      },
      {
        id: 3,
        name: "Kochi Store",
        pincode: 682001,
        lat: 9.9312,
        lng: 76.2673,
      },
    ];

    // Pincode to coordinates mapping
    this.pincodeMap = {
      676505: { lat: 11.0735, lng: 76.068 },
      673001: { lat: 11.2588, lng: 75.7804 },
      682001: { lat: 9.9312, lng: 76.2673 },
      680001: { lat: 10.0333, lng: 76.3333 }, // Kochi general
      679001: { lat: 11.8745, lng: 75.3704 }, // Kozhikode general
      676001: { lat: 11.0043, lng: 76.2144 }, // Malappuram general
    };

    this.selectedStore = null;
    this.init();
  }

  init() {
    if (!this.cacheDOM()) {
      // If elements not found, retry after a short delay
      setTimeout(() => this.init(), 500);
      return;
    }
    this.loadStoredStore();
    this.renderStoreList();
    this.bindEvents();
    this.updateButtonText();
  }

  cacheDOM() {
    this.button = document.getElementById("store-selector-btn");
    this.modal = document.getElementById("store-selector-modal");
    this.overlay = document.getElementById("store-selector-modal-overlay");
    this.closeBtn = document.getElementById("store-selector-modal-close");
    this.storeList = document.getElementById("store-selector-list");
    this.pincodeForm = document.getElementById("store-selector-pincode-form");
    this.pincodeInput = document.getElementById("pincode-input");
    this.pincodeError = document.getElementById("pincode-error");
    this.pincodeResult = document.getElementById("pincode-result");
    this.resultStoreName = document.getElementById("result-store-name");
    this.confirmNearestBtn = document.getElementById("confirm-nearest-store");
    this.tabs = document.querySelectorAll(".store-selector-tab");
    this.buttonText = document.getElementById("store-selector-text");
    this.nearestStore = null;

    // Verify critical elements exist
    if (!this.button || !this.modal || !this.storeList) {
      console.error("Store Selector: Required DOM elements not found");
      return false;
    }
    return true;
  }

  bindEvents() {
    // Verify elements exist before binding
    if (!this.button) {
      console.warn(
        "Store Selector: Button element not found, retrying in 500ms"
      );
      setTimeout(() => this.bindEvents(), 500);
      return;
    }

    // Button and modal controls
    if (this.button)
      this.button.addEventListener("click", () => this.toggleModal());
    if (this.closeBtn)
      this.closeBtn.addEventListener("click", () => this.closeModal());
    if (this.overlay)
      this.overlay.addEventListener("click", () => this.closeModal());

    // Store selection
    if (this.storeList)
      this.storeList.addEventListener("click", (e) =>
        this.handleStoreSelect(e)
      );

    // Pincode form
    if (this.pincodeForm) {
      this.pincodeForm.addEventListener("submit", (e) =>
        this.handlePincodeSubmit(e)
      );
    }
    if (this.confirmNearestBtn) {
      this.confirmNearestBtn.addEventListener("click", () =>
        this.confirmNearestStore()
      );
    }

    // Tab switching
    if (this.tabs && this.tabs.length > 0) {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => this.switchTab(tab.dataset.tab));
      });
    }

    // Keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      if (
        e.key === "Escape" &&
        this.modal &&
        this.modal.style.display !== "none"
      ) {
        this.closeModal();
      }
    });
  }

  /**
   * Render store list options
   */
  renderStoreList() {
    this.storeList.innerHTML = this.stores
      .map(
        (store) => `
      <div class="store-selector-item" data-store-id="${store.id}">
        <p class="store-selector-item-name">${store.name}</p>
        <p class="store-selector-item-pincode">📍 ${store.pincode}</p>
      </div>
    `
      )
      .join("");
  }

  /**
   * Toggle modal visibility
   */
  toggleModal() {
    const isOpen = this.modal.style.display !== "none";
    isOpen ? this.closeModal() : this.openModal();
  }

  /**
   * Open modal
   */
  openModal() {
    this.modal.style.display = "block";
    this.overlay.style.display = "block";
    this.button.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";

    // Focus on modal for accessibility
    this.modal.focus();
  }

  /**
   * Close modal
   */
  closeModal() {
    this.modal.style.display = "none";
    this.overlay.style.display = "none";
    this.button.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";

    // Reset pincode form
    this.pincodeInput.value = "";
    this.pincodeError.style.display = "none";
    this.pincodeResult.style.display = "none";

    // Return focus to button
    this.button.focus();
  }

  /**
   * Handle store selection
   */
  handleStoreSelect(e) {
    const storeItem = e.target.closest(".store-selector-item");
    if (!storeItem) return;

    const storeId = parseInt(storeItem.dataset.storeId);
    const store = this.stores.find((s) => s.id === storeId);

    if (store) {
      this.selectStore(store);
      this.closeModal();
    }
  }

  /**
   * Select a store and save it
   */
  selectStore(store) {
    this.selectedStore = store;
    this.saveStoreToLocalStorage(store);
    this.saveStoreToCartAttribute(store);
    this.updateButtonText();
    this.updateStoreListUI();
  }

  /**
   * Update button text to show selected store
   */
  updateButtonText() {
    if (this.selectedStore) {
      this.buttonText.textContent = `Store: ${this.selectedStore.name}`;
    } else {
      this.buttonText.textContent = "Select Store";
    }
  }

  /**
   * Update visual state of store list
   */
  updateStoreListUI() {
    document.querySelectorAll(".store-selector-item").forEach((item) => {
      const storeId = parseInt(item.dataset.storeId);
      if (this.selectedStore && storeId === this.selectedStore.id) {
        item.classList.add("store-selector-item--selected");
      } else {
        item.classList.remove("store-selector-item--selected");
      }
    });
  }

  /**
   * Switch between manual and pincode tabs
   */
  switchTab(tabName) {
    // Update active tab
    this.tabs.forEach((tab) => {
      if (tab.dataset.tab === tabName) {
        tab.classList.add("store-selector-tab--active");
        tab.setAttribute("aria-selected", "true");
      } else {
        tab.classList.remove("store-selector-tab--active");
        tab.setAttribute("aria-selected", "false");
      }
    });

    // Update content visibility
    document
      .querySelectorAll(".store-selector-tab-content")
      .forEach((content) => {
        if (content.dataset.tabContent === tabName) {
          content.classList.add("store-selector-tab-content--active");
        } else {
          content.classList.remove("store-selector-tab-content--active");
        }
      });
  }

  /**
   * Handle pincode form submission
   */
  handlePincodeSubmit(e) {
    e.preventDefault();

    const pincode = this.pincodeInput.value.trim();

    // Validation
    if (!pincode || pincode.length !== 6 || !/^\d+$/.test(pincode)) {
      this.showPincodeError("Please enter a valid 6-digit pincode");
      return;
    }

    this.pincodeError.style.display = "none";

    // Get coordinates from pincode map
    const coordinates = this.pincodeMap[pincode];

    if (!coordinates) {
      this.showPincodeError("Pincode not found in our service area");
      return;
    }

    // Find nearest store
    const nearest = this.findNearestStore(coordinates.lat, coordinates.lng);

    if (nearest) {
      this.nearestStore = nearest;
      this.displayNearestStore(nearest);
    }
  }

  /**
   * Show pincode error message
   */
  showPincodeError(message) {
    this.pincodeError.textContent = message;
    this.pincodeError.style.display = "block";
    this.pincodeResult.style.display = "none";
  }

  /**
   * Find nearest store using Haversine formula
   */
  findNearestStore(userLat, userLng) {
    let nearest = null;
    let minDistance = Infinity;

    this.stores.forEach((store) => {
      const distance = this.calculateDistance(
        userLat,
        userLng,
        store.lat,
        store.lng
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearest = { ...store, distance: distance.toFixed(2) };
      }
    });

    return nearest;
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   * Returns distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  /**
   * Display nearest store result
   */
  displayNearestStore(store) {
    this.resultStoreName.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div>
          <p style="margin: 0; font-weight: 600;">${store.name}</p>
          <p style="margin: 4px 0 0 0; font-size: 12px; color: #666;">📍 ${store.distance} km away</p>
        </div>
      </div>
    `;
    this.pincodeResult.style.display = "block";
  }

  /**
   * Confirm and select the nearest store
   */
  confirmNearestStore() {
    if (this.nearestStore) {
      this.selectStore(this.nearestStore);
      this.closeModal();
      this.switchTab("manual"); // Reset to manual tab
    }
  }

  /**
   * Save store to localStorage
   */
  saveStoreToLocalStorage(store) {
    try {
      localStorage.setItem(
        "selectedStore",
        JSON.stringify({
          id: store.id,
          name: store.name,
          pincode: store.pincode,
          selectedAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.warn("Failed to save store to localStorage:", error);
    }
  }

  /**
   * Load store from localStorage
   */
  loadStoredStore() {
    try {
      const stored = localStorage.getItem("selectedStore");
      if (stored) {
        const storedData = JSON.parse(stored);
        this.selectedStore = this.stores.find((s) => s.id === storedData.id);
        if (this.selectedStore) {
          this.updateStoreListUI();
        }
      }
    } catch (error) {
      console.warn("Failed to load store from localStorage:", error);
    }
  }

  /**
   * Save store to Shopify cart attributes via AJAX
   */
  saveStoreToCartAttribute(store) {
    // Prepare cart attribute data
    const cartAttributes = {
      selected_store: store.name,
      selected_store_id: store.id.toString(),
      selected_store_pincode: store.pincode.toString(),
    };

    // Update cart attributes using Shopify's Cart API
    fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        attributes: cartAttributes,
      }),
    })
      .then((response) => response.json())
      .then((cart) => {
        console.log("Cart updated with store selection:", cart);
      })
      .catch((error) => {
        console.warn("Failed to update cart attributes:", error);
      });
  }

  /**
   * Get currently selected store
   */
  getSelectedStore() {
    return this.selectedStore;
  }

  /**
   * Clear selected store
   */
  clearSelectedStore() {
    this.selectedStore = null;
    try {
      localStorage.removeItem("selectedStore");
    } catch (error) {
      console.warn("Failed to clear localStorage:", error);
    }
    this.updateButtonText();
    this.updateStoreListUI();
  }
}

// Initialize Store Selector when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  console.log("DOMContentLoaded: Initializing Store Selector");
  window.storeSelector = new StoreSelector();
  console.log("Store Selector initialized", window.storeSelector);
});

// Also handle case where script loads after DOM is ready
if (document.readyState === "loading") {
  console.log("Document is loading, will initialize on DOMContentLoaded");
  document.addEventListener("DOMContentLoaded", () => {
    if (!window.storeSelector) {
      console.log("DOMContentLoaded: Late init for Store Selector");
      window.storeSelector = new StoreSelector();
    }
  });
} else {
  console.log(
    "Document already loaded, initializing Store Selector immediately"
  );
  if (!window.storeSelector) {
    window.storeSelector = new StoreSelector();
    console.log("Store Selector initialized", window.storeSelector);
  }
}
